import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { getChessCoachStatus } from '../../api/playing';
import { useChessCoach } from '../../context/ChessCoachContext';
import ChessCoachPanel from './ChessCoachPanel';

function parseGameIdFromPath(pathname) {
  const m = String(pathname || '').match(/\/playing\/(?:game|spectate)\/(\d+)/);
  return m ? Number(m[1]) : null;
}

/**
 * Site-wide floating ChessDesk Coach — launcher + chat window on every authenticated page.
 */
export default function ChessCoachWidget() {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const {
    isOpen, openCoach, closeCoach, toggleCoach, gameContext,
  } = useChessCoach();
  const [coachLive, setCoachLive] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getChessCoachStatus()
      .then((s) => {
        if (!cancelled) setCoachLive(!!(s?.enabled && s?.configured));
      })
      .catch(() => {
        if (!cancelled) setCoachLive(false);
      });
    return () => { cancelled = true; };
  }, []);

  const urlGameId = useMemo(() => {
    const fromPath = parseGameIdFromPath(location.pathname);
    if (fromPath) return fromPath;
    const raw = searchParams.get('gameId');
    return raw && /^\d+$/.test(raw) ? Number(raw) : null;
  }, [location.pathname, searchParams]);

  const urlFocusPly = useMemo(() => {
    const raw = searchParams.get('ply');
    return raw && /^\d+$/.test(raw) ? Number(raw) : null;
  }, [searchParams]);

  const gameId = gameContext?.gameId ?? urlGameId;
  const focusPly = gameContext?.focusPly ?? urlFocusPly;

  // Live game / spectate pages use the board-tethered GameBoardCoach instead.
  const onBoardGamePage = /\/playing\/(?:game|spectate)\/\d+/.test(location.pathname || '');

  // Deep link: /playing/ai-coach opens the widget automatically
  useEffect(() => {
    if (location.pathname.replace(/\/+$/, '') === '/playing/ai-coach') {
      openCoach({ gameId: urlGameId, focusPly: urlFocusPly });
    }
  }, [location.pathname, openCoach, urlFocusPly, urlGameId]);

  if (onBoardGamePage) return null;
  if (coachLive === false) return null;
  if (coachLive === null) return null;

  return (
    <div className="playing-coach-widget" data-open={isOpen ? 'true' : 'false'}>
      {!isOpen && (
        <button
          type="button"
          className="playing-coach-widget__launcher"
          onClick={toggleCoach}
          aria-label={t('playing.coach.openChat', { defaultValue: 'Open Ask AI Coach' })}
          title={t('playing.coach.title', { defaultValue: 'Ask AI Coach' })}
        >
          <span className="playing-coach-widget__launcher-icon" aria-hidden>
            <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="40" height="40" rx="12" fill="url(#coachLauncherGrad)" />
              <path
                d="M20 9c-1.2 0-2.2.7-2.6 1.8L15.8 15H12l1.4 2.2c.4.6.4 1.4 0 2L12 21.5h3.2l.6 2.4c.3 1.1 1.3 1.9 2.5 1.9h3.4c1.2 0 2.2-.8 2.5-1.9l.6-2.4H28l-1.4-2.3c-.4-.6-.4-1.4 0-2L28 15h-3.8l-1.6-4.2C22.2 9.7 21.2 9 20 9zm-1.5 18.5h3c.8 0 1.5.7 1.5 1.5v1h-6v-1c0-.8.7-1.5 1.5-1.5z"
                fill="white"
                fillOpacity="0.95"
              />
              <defs>
                <linearGradient id="coachLauncherGrad" x1="6" y1="4" x2="34" y2="36" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#0f766e" />
                  <stop offset="1" stopColor="#0369a1" />
                </linearGradient>
              </defs>
            </svg>
          </span>
          <span className="playing-coach-widget__launcher-label">
            {t('playing.coach.shortTitle', { defaultValue: 'Ask AI Coach' })}
          </span>
        </button>
      )}

      {isOpen && (
        <div
          className="playing-coach-widget__window"
          role="dialog"
          aria-modal="false"
          aria-label={t('playing.coach.title', { defaultValue: 'Ask AI Coach' })}
        >
          <header className="playing-coach-widget__header">
            <div className="playing-coach-widget__header-main">
              <p className="playing-coach-widget__header-title">
                {t('playing.coach.title', { defaultValue: 'Ask AI Coach' })}
              </p>
              <p className="playing-coach-widget__header-sub">
                {gameId != null
                  ? t('playing.coach.widgetGameHint', { defaultValue: 'Discussing this game' })
                  : t('playing.coach.widgetGeneralHint', { defaultValue: 'Ask anything about chess' })}
              </p>
            </div>
            <button
              type="button"
              className="playing-coach-widget__icon-btn"
              onClick={closeCoach}
              aria-label={t('playing.coach.minimize', { defaultValue: 'Minimize chat' })}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14" strokeLinecap="round" />
              </svg>
            </button>
          </header>
          <div className="playing-coach-widget__body">
            <ChessCoachPanel
              embedded
              gameId={gameId}
              focusPly={focusPly}
            />
          </div>
        </div>
      )}
    </div>
  );
}
